import type { WorldDocument } from "../document/types.ts";
import { hasOnly, isIntegerIn, recordOf } from "./op-read.ts";
import type { LiveOp } from "./op-types.ts";
import type { IntentOutcome } from "./resolve-types.ts";
import { opOf } from "./ops.ts";
import { isEditorId } from "./protocol-shared.ts";
import { isCount, type ClaimTarget, type FrozenReason } from "./protocol-stream.ts";
import type { LiveServerMessage } from "./protocol.ts";

/**
 * The shapes of a world's room on the build server (ADR-086 decisions 21 to 24 and 29): its state as plain
 * data, what a step of its reducer returns, the line an accepted operation is journaled as and the counters a
 * journal carries, each of the last two with its codec.
 */

/**
 * The most operations the ring keeps for a tab that resumes.
 */
export const RING_OPS = 2048;

/**
 * The most bytes the ring's operations take together, each counted as the UTF-8 bytes of its own encoding.
 */
export const RING_BYTES = 8 * 1024 * 1024;

/**
 * The most counters a room keeps in all, one a sender's tab tag. One past it drops the counter longest without
 * an operation.
 */
export const ROOM_TAGS_MAX = 1024;

/**
 * The most counters a room keeps for one sender's key. One past it drops that key's own counter longest
 * without an operation, so a sender can only ever cost itself a counter.
 */
export const KEY_TAGS_MAX = 64;

/**
 * The most contributors a room names for a flush. One past it drops the one longest without an operation.
 */
export const CONTRIBUTORS_MAX = 32;

/**
 * How long a claim lasts without an operation of its holder inside it.
 */
export const CLAIM_IDLE_MS = 900_000;

/**
 * How long a claim lasts after its holder's socket left.
 */
export const CLAIM_GONE_MS = 60_000;

/**
 * The longest key a host names a sender by, in UTF-16 code units.
 */
export const ROOM_KEY_MAX = 256;

/**
 * The largest counter an operation's id carries: fifteen digits.
 */
export const OP_COUNTER_MAX = 999_999_999_999_999;

/**
 * Who a message to the room came from, as the host knows them: the id it gave the socket, the name it shows,
 * and a key that stays the same for one builder across sockets (an account), which the room counts operations
 * and claims under and never puts in a message.
 */
export interface RoomSender {
  readonly id: string;
  readonly name: string;
  readonly key: string;
}

/**
 * An accepted operation as it is journaled: its sequence, when it was accepted, the socket and the sender's
 * key it came from, and the operation.
 */
export interface RoomEntry {
  readonly seq: number;
  readonly at: number;
  readonly by: string;
  readonly key: string;
  readonly op: LiveOp;
}

/**
 * An accepted operation as the ring keeps it, with the bytes it counts for.
 */
export interface RingEntry extends RoomEntry {
  readonly bytes: number;
}

/**
 * The highest counter a room accepted under one tab tag of one sender's key, and when.
 */
export interface TagCounter {
  readonly key: string;
  readonly tag: string;
  readonly n: number;
  readonly at: number;
}

/**
 * How many operations a sender's key contributed since the last flush.
 */
export interface Contributor {
  readonly key: string;
  readonly ops: number;
}

/**
 * A claim as the room keeps it: the socket that holds it, its holder's key and name, what it covers, when its
 * holder last worked inside it and when its holder's socket left, null while it is there.
 */
export interface RoomClaim {
  readonly by: string;
  readonly key: string;
  readonly name: string;
  readonly target: ClaimTarget;
  readonly touched: number;
  readonly left: number | null;
}

/**
 * A flush that is running: the sequence it writes, and what was unflushed when it started.
 */
export interface RoomFlushing {
  readonly seq: number;
  readonly since: number;
  readonly contributors: readonly Contributor[];
}

/**
 * What a flush writes: the document at a sequence, the first sequence the head does not hold yet, since when
 * that was unflushed, who contributed, the last contributor last, and the counters as they stand at that
 * sequence, which a journal rewritten to start from this flush carries in place of the operations it drops.
 */
export interface RoomFlush extends RoomFlushing {
  readonly from: number;
  readonly document: WorldDocument;
  readonly counters: readonly TagCounter[];
}

/**
 * A room: the document every tab converges on, the epoch of this opening, the sequence of the last operation
 * accepted, the head's revision and the sequence it holds, the ring of the last operations with the bytes they
 * take, each sender's counters, since when something is unflushed and who contributed, the flush that is
 * running, the claims, and why the room is frozen when it is. Plain data; no step writes into it.
 */
export interface RoomState {
  readonly document: WorldDocument;
  readonly epoch: string;
  readonly seq: number;
  readonly revision: number;
  readonly flushed: number;
  readonly ring: readonly RingEntry[];
  readonly ringBytes: number;
  readonly counters: readonly TagCounter[];
  readonly unflushedSince: number | null;
  readonly contributors: readonly Contributor[];
  readonly flushing: RoomFlushing | null;
  readonly claims: readonly RoomClaim[];
  readonly frozen: FrozenReason | null;
}

/**
 * Why a step did not do what its message asked: the operation's id is `invalid`; the room is `frozen`; the
 * id was `resent` (at or below its sender's counter for the tag); the resume speaks another `version`; the
 * editor holds its four claims (`editor-claims`); the world holds its thirty-two (`world-claims`); the editor
 * holds no such claim to release (`no-claim`).
 */
export type RoomRefusal = "invalid" | "frozen" | "resent" | "version" | "editor-claims" | "world-claims" | "no-claim";

/**
 * A message a step sends: to the sender alone, to every other socket in the stream, or to all of them.
 */
export interface RoomSend {
  readonly to: "sender" | "others" | "all";
  readonly message: LiveServerMessage;
}

/**
 * What a step of the reducer returns: the room after it (the room given when nothing changed), what to send
 * to whom in order, why the message was refused if it was, and the line to journal before anything is sent
 * when an operation was sequenced.
 */
export interface RoomStep {
  readonly state: RoomState;
  readonly send: readonly RoomSend[];
  readonly refused: RoomRefusal | null;
  readonly entry: RoomEntry | null;
  /** Each intent's outcome, in the operation's order, when the step resolved an operation. */
  readonly outcomes?: readonly IntentOutcome[];
}

const TAG = /^[a-z0-9]{1,16}$/;
const KEY_CONTROL = /[\x00-\x1f\x7f]/;

/**
 * Whether a value is a key a host names a sender by.
 *
 * @param value The value.
 * @returns True for a string of 1 to `ROOM_KEY_MAX` code units with no control character.
 */
export function isRoomKey(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= ROOM_KEY_MAX && !KEY_CONTROL.test(value);
}

/**
 * Reads a journaled operation from a value already parsed.
 *
 * @param value The value.
 * @returns The entry rebuilt with exactly its fields, or null when the value is not exactly one: a sequence
 *   from 1, a time in whole milliseconds from 0, an editor's id, a sender's key and an operation `opOf` reads.
 */
export function entryOf(value: unknown): RoomEntry | null {
  const fields = recordOf(value);
  if (fields === undefined || !hasOnly(fields, ["seq", "at", "by", "key", "op"])) {
    return null;
  }
  const { seq, at, by, key } = fields;
  const op = isCount(seq, 1) && isCount(at, 0) && isEditorId(by) && isRoomKey(key) ? opOf(fields["op"]) : null;
  return op === null ? null : { seq: seq as number, at: at as number, by: by as string, key: key as string, op };
}

/**
 * Writes a journaled operation as one line of text.
 *
 * @param entry The entry.
 * @returns Its JSON, which holds no line break.
 */
export function encodeEntry(entry: RoomEntry): string {
  return JSON.stringify({ seq: entry.seq, at: entry.at, by: entry.by, key: entry.key, op: entry.op });
}

/**
 * Reads the counters a journal carries from a value already parsed.
 *
 * @param value The value.
 * @returns The counters rebuilt with exactly their fields, or null when the value is not a list of at most
 *   `ROOM_TAGS_MAX` of them and `KEY_TAGS_MAX` a key, one a key and tag, each a counter from 1 to
 *   `OP_COUNTER_MAX` and a time from 0.
 */
export function countersOf(value: unknown): TagCounter[] | null {
  if (!Array.isArray(value) || value.length > ROOM_TAGS_MAX) {
    return null;
  }
  const counters: TagCounter[] = [];
  for (const item of value as readonly unknown[]) {
    const fields = recordOf(item);
    if (fields === undefined || !hasOnly(fields, ["key", "tag", "n", "at"])) {
      return null;
    }
    const { key, tag, n, at } = fields;
    const own = isRoomKey(key) ? counters.filter((held) => held.key === key) : [];
    if (!isRoomKey(key) || typeof tag !== "string" || !TAG.test(tag) || !isIntegerIn(n, 1, OP_COUNTER_MAX) || !isCount(at, 0) || own.length >= KEY_TAGS_MAX || own.some((held) => held.tag === tag)) {
      return null;
    }
    counters.push({ key, tag, n, at });
  }
  return counters;
}
